import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Typography, Paper, MenuItem, Select, FormControl, InputLabel, Grid, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Divider, FormControlLabel } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getUnitDetails, updateLevelById, updateAudioFileForQuestion, archiveQuestionsForLevel, restoreQuestionsForLevel } from '../service/service';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import AudioPlayerComponent from '../components/AudioPlayer';
import NewQuestionForm from '../components/NewQuestionForm';
import ConfirmationModal from '../components/ConfirmationModal';
import FilterMenu from '../components/FilterMenu';
import Layout from '../components/Layout';


const questionTypes = ["MC", "MCM", "ACIQ", "ACMC", "CT", "ACIQM", "UVI", "LAR", "CTA"]; // Example options for question type

const hintTypes = ["audio", "text"];

// Create a custom alert component
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UnitEditPage = () => {
  const { courseId, unitId } = useParams();
  const navigate = useNavigate();
  const [unitData, setUnitData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [originalLevels, setOriginalLevels] = useState([]);
  const [selectedQuestionsByLevel, setSelectedQuestionsByLevel] = useState({}); // Track selected question ids per level.
  const [archivingLevelId, setArchivingLevelId] = useState(null);
  const [restoringLevelId, setRestoringLevelId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');

  const [file, setFile] = useState(null); // State to store the file
  const [uploadStatus, setUploadStatus] = useState("");

  const [activeLevelId, setActiveLevelId] = useState(null);
  const [questionFilter, setQuestionFilter] = useState('active');

  const filterLabels = {
    active: 'Active questions',
    archived: 'Archived questions',
    all: 'All questions',
  };

  const questionFilterMenuLabel = filterLabels[questionFilter] || filterLabels.active;

  const shouldDisplayQuestion = (filter, isQuestionArchived) => {
    switch (filter) {
      case 'archived':
        return isQuestionArchived;
      case 'all':
        return true;
      case 'active':
      default:
        return !isQuestionArchived;
    }
  };

  const handleCancelNewQuestion = () => {
    setActiveLevelId(null);
  };

  const handleQuestionFormSuccess = (newQuestion, context = {}) => {
    const targetLevelId =
      context?.question?.parentLevelId || newQuestion?.parentLevelId || activeLevelId;

    if (!targetLevelId || !newQuestion) {
      return;
    }

    // Append the new question to the appropriate level
    setLevels((prevLevels) =>
      prevLevels.map((level) =>
        level._id === targetLevelId
          ? {
              ...level,
              questions: [...(level.questions || []), newQuestion],
            }
          : level
      )
    );

    setActiveLevelId(null);
    if (!context?.response?.success) {
      setSeverity("error");
      setMessage(context?.response?.error || "Question created with issues.");
    } else { 
      setSeverity("success");
      setMessage('Question added successfully!');
    }
    setOpen(true);
  };

  const fileInputRef = useRef(null);
  useEffect(() => {
    const fetchUnitDetails = async () => {
      try {
        const { data } = await getUnitDetails(courseId, unitId);
        setUnitData(data.unit);
        setLevels(data.levels);
        setSelectedQuestionsByLevel({});
      } catch (error) {
        console.error('Failed to fetch unit details:', error);
      }
    };
    fetchUnitDetails();
  }, [unitId]);


  const handleLevelChange = (index, key, value) => {
    const updatedLevels = levels.map((level, i) => (i === index ? { ...level, [key]: value } : level));
    setLevels(updatedLevels);
  };

  const handleQuestionChange = (levelIndex, questionIndex, key, value) => {
    const updatedLevels = [...levels];
    updatedLevels[levelIndex].questions[questionIndex][key] = value;
    updatedLevels[levelIndex].questions[questionIndex].isModified = true;
    // const updatedLevels = levels.map((level, i) => 
    //   i === levelIndex
    //     ? { ...level, questions: level.questions.map((q, j) => (j === questionIndex ? { ...q, [key]: value } : q)) }
    //     : level
    // );

    setLevels(updatedLevels);
  };

  const handleOptionChange = (levelIndex, questionIndex, optionIndex, value) => {
    const updatedLevels = [...levels];

    const { questionType, Answer } = updatedLevels[levelIndex].questions[questionIndex];
    const oldOptionValue = updatedLevels[levelIndex].questions[questionIndex].options[optionIndex]
    if (questionType === 'MCM') {
      const answerArray = Answer.split('; ');
      if (answerArray.includes(oldOptionValue)) {
        const newAnswer = answerArray.filter(option => option !== oldOptionValue);
        newAnswer.push(value);
        updatedLevels[levelIndex].questions[questionIndex].Answer = newAnswer.join('; ');
      }
    } else {
      if (oldOptionValue === Answer) {
        updatedLevels[levelIndex].questions[questionIndex].Answer = value;
      }
    }

    updatedLevels[levelIndex].questions[questionIndex].options[optionIndex] = value;
    updatedLevels[levelIndex].questions[questionIndex].isModified = true;
    setLevels(updatedLevels);
  };

  const handleAnswerChange = (levelIndex, questionIndex, questionType, Answer) => {
    const updatedLevels = [...levels];

    switch(questionType) {
      case 'MC': {
        updatedLevels[levelIndex].questions[questionIndex].Answer = Answer;
        break;
      }
      case 'ACIQ': {
        updatedLevels[levelIndex].questions[questionIndex].Answer = Answer;
        break;
      }

      case 'ACMC': {
        updatedLevels[levelIndex].questions[questionIndex].Answer = Answer;
        break;
      }
      default:
        console.log("Unknown questionType", questionType);
    }
    updatedLevels[levelIndex].questions[questionIndex].isModified = true;
    setLevels(updatedLevels);
  }

  // Toggle per-question selection for upcoming bulk archive.
  const toggleQuestionSelection = (levelId, questionId, isChecked) => {
    setSelectedQuestionsByLevel((prev) => {
      const current = new Set(prev[levelId] || []);
      if (isChecked) {
        current.add(questionId);
      } else {
        current.delete(questionId);
      }

      // Update the selected questions for this level
      const next = { ...prev };
      if (current.size) {
        next[levelId] = Array.from(current);
      } else {
        delete next[levelId];
      }

      return next;
    });
  };

  // Mark selected questions as archived in the backend, then clean them up locally.
  const handleArchiveSelectedQuestions = async (levelId) => {
    const targetLevel = levels.find((level) => level._id === levelId);
    if (!targetLevel) {
      return;
    }

    const idsForLevel = (selectedQuestionsByLevel[levelId] || []).filter((questionId) => {
      const question = targetLevel.questions?.find((item) => item._id === questionId);
      return question && !question.isQuestionArchived;
    });

    if (!idsForLevel.length) {
      return;
    }

    setArchivingLevelId(levelId);
    try {
      const response = await archiveQuestionsForLevel(levelId, idsForLevel);

      // Update local state to remove archived questions
      setLevels((prevLevels) =>
        prevLevels.map((level) => {
          if (level._id !== levelId) {
            return level;
          }
          const idsToMark = new Set(idsForLevel);

          const markedQuestions = level.questions.map((question) =>
            idsToMark.has(question._id)
              ? { ...question, isQuestionArchived: true, order: null }
              : { ...question }
          );

          const visibleQuestions = markedQuestions
            .filter((question) => !question.isQuestionArchived)
            .sort((a, b) => a.order - b.order);

          const orderLookup = new Map();
          visibleQuestions.forEach((question, index) => {
            orderLookup.set(question._id, index + 1);
          });

          const normalizedQuestions = markedQuestions.map((question) =>
            question.isQuestionArchived
              ? question
              : { ...question, order: orderLookup.get(question._id) || question.order }
          );

          return {
            ...level,
            questions: normalizedQuestions,
          };
        })
      );

      // Clear selected questions for this level after deletion
      setSelectedQuestionsByLevel((prev) => {
        const next = { ...prev };
        const remainingIds = (next[levelId] || []).filter((id) => !idsForLevel.includes(id));
        if (remainingIds.length) {
          next[levelId] = remainingIds;
        } else {
          delete next[levelId];
        }
        return next;
      });

      setSeverity('success');
      setMessage(response?.message || 'Selected questions removed.');

      return response;
    } catch (error) {
      console.error('Failed to archive questions for level:', error);
      const errorMessage = error?.message || error?.error || 'Failed to archive selected questions.';
      setSeverity('error');
      setMessage(errorMessage);
      throw error;
    } finally {
      setOpen(true);
      setArchivingLevelId(null);
    }
  };

  const handleRestoreSelectedQuestions = async (levelId) => {
    const targetLevel = levels.find((level) => level._id === levelId);
    if (!targetLevel) {
      return;
    }

    const idsForLevel = (selectedQuestionsByLevel[levelId] || []).filter((questionId) => {
      const question = targetLevel.questions?.find((item) => item._id === questionId);
      return question && question.isQuestionArchived;
    });

    if (!idsForLevel.length) {
      return;
    }

    setRestoringLevelId(levelId);
    try {
      const response = await restoreQuestionsForLevel(levelId, idsForLevel);
      const restoredMeta = response?.data?.updatedQuestions || [];
      const restoredLookup = new Map(restoredMeta.map((item) => [item._id, item]));

      setLevels((prevLevels) =>
        prevLevels.map((level) => {
          if (level._id !== levelId) {
            return level;
          }

          const updatedQuestions = level.questions.map((question) => {
            const restored = restoredLookup.get(question._id);
            if (!restored) {
              return question;
            }

            return {
              ...question,
              isQuestionArchived: false,
              order: restored.order,
            };
          });

          return {
            ...level,
            questions: updatedQuestions,
          };
        })
      );

      setSelectedQuestionsByLevel((prev) => {
        const next = { ...prev };
        const remainingIds = (next[levelId] || []).filter((id) => !idsForLevel.includes(id));
        if (remainingIds.length) {
          next[levelId] = remainingIds;
        } else {
          delete next[levelId];
        }
        return next;
      });

      setSeverity('success');
      setMessage(response?.message || 'Selected questions restored.');

      return response;
    } catch (error) {
      console.error('Failed to restore questions for level:', error);
      const errorMessage = error?.message || error?.error || 'Failed to restore selected questions.';
      setSeverity('error');
      setMessage(errorMessage);
      throw error;
    } finally {
      setOpen(true);
      setRestoringLevelId(null);
    }
  };

  const handleUpdateLevel = async (levelId, levelData, levelIndex) => {
    setLoading(true);
    try {
      const modifiedQuestions = levelData.questions.filter((questionData) => questionData.isModified);
      // levelData.questions = modifiedQuestions;
      console.log(">> Level Data: ", levelData);
      const res = await updateLevelById(levelId, levelData);
      console.log("Updated Res --- ---", res);
      if (res.isValid) {
        setMessage(res.message); // Set the success message
        setOpen(true); // Open the toast
      } else {
        setMessage('Error occurred while updating levels and question');
        setSeverity('error'); // Red toast for error
      }
    } catch (error) {
      console.error('Failed to update level:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false); // Close the toast when done
  };

  const isOptionCorrect = (option, answer, type) => {
    let result = false;

    switch(type) {
      case 'MC': {
        result = option?.toLowerCase() === answer.toLowerCase();
        break;
      }

      case 'ACIQ': {
        result = option?.toLowerCase() === answer.toLowerCase();
        break;
      }
      case 'ACIQM': {
        result = answer?.split('; ').includes(option);
        break;
      }

      case 'MCM': {
        result = answer?.split('; ').includes(option);
        break;
      }
      default: 
        console.log("Unknown type", type);
    }

    return result
  };


    // Handle file selection
    const handleFileSelectOfReplaceAudio = (e) => {
      const selectedFile = e.target.files[0];
      if (selectedFile && selectedFile.type === "audio/mpeg") {
        setFile(selectedFile);
      } else {
        alert("Please upload a valid MP3 file.");
      }
    };
  
    // Handle form submission
    const replaceAudio = async (e, question, type) => {
      e.preventDefault();
  
      const fieldToUpdate = type;
      if (!file) {
        alert("Please select a file to upload.");
        return;
      }
  
      // Prepare the form data
      const formData = new FormData();
      formData.append("file", file); // Add the file to the form data
      formData.append("questionId", question._id); // Replace with actual data
      formData.append("courseId", question.parentCourseId); // Replace with actual data
      formData.append("unitId", question.parentUnitId); // Replace with actual data
      formData.append("levelId",  question.parentLevelId); // Replace with actual data
      formData.append("fieldToUpdate", fieldToUpdate); // Replace with actual data

      const res = await updateAudioFileForQuestion(formData);
      console.log("Res of replace audio", res);

      if (res.message) {
        alert("File Uploaded Successfully")
      } else {
        alert("Something went wrong!!")
      }

      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset the file input field
      }

      // try {
      //   // Post the file to your backend
      //   const response = await axios.post("http://localhost:5000/api/upload", formData, {
      //     headers: {
      //       "Content-Type": "multipart/form-data", // Required for file uploads
      //     },
      //   });
  
      //   setUploadStatus("File uploaded successfully!");
      //   console.log("Response:", response.data);
      // } catch (error) {
      //   setUploadStatus("File upload failed.");
      //   console.error("Error uploading file:", error);
      // }
    };



  if (!unitData) return <Typography>Loading...</Typography>;

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100%', width: '100%', backgroundColor: 'background.default', p: 4 }}>
        <Paper elevation={0} sx={{ padding: '2rem', width: '100%' }}>
          {/* Unit Information (Read-only) */}
          <Typography variant="h5" gutterBottom>Edit Unit: {unitData.name}</Typography>
          <Typography variant="subtitle1">Order: {unitData.order}</Typography>
          <Typography variant="subtitle1">Bonus: {unitData.isBonus ? 'Yes' : 'No'}</Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <FilterMenu
              label={questionFilterMenuLabel}
              selectedValue={questionFilter}
              onChange={setQuestionFilter}
            />
          </Box>

          {/* Level Sections */}
          {levels.map((level, levelIndex) => {
            const selectedQuestionsForLevel = selectedQuestionsByLevel[level._id] || [];
            // Count of visible questions in this level
            const visibleQuestionCount = level.questions?.filter((question) => !question.isQuestionArchived).length || 0;
            const selectedActiveQuestions = level.questions?.filter(
              (question) => selectedQuestionsForLevel.includes(question._id) && !question.isQuestionArchived
            ) || [];
            const selectedArchivedQuestions = level.questions?.filter(
              (question) => selectedQuestionsForLevel.includes(question._id) && question.isQuestionArchived
            ) || [];
            const hasActiveSelections = selectedActiveQuestions.length > 0;
            const hasArchivedSelections = selectedArchivedQuestions.length > 0;
            const orderedQuestions = [...(level.questions || [])].sort((a, b) => {
              const aArchived = Boolean(a?.isQuestionArchived);
              const bArchived = Boolean(b?.isQuestionArchived);
              if (aArchived !== bArchived) {
                return aArchived ? 1 : -1;
              }

              const aOrder = typeof a?.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
              const bOrder = typeof b?.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
              if (aOrder !== bOrder) {
                return aOrder - bOrder;
              }

              const aQuestion = a?.question || '';
              const bQuestion = b?.question || '';
              return aQuestion.localeCompare(bQuestion);
            });

            return (
            <Paper
              key={level._id}
              sx={{ 
                mt: 3,
                mb: 3,
                padding: '24px',
                border: '1px solid silver',
              }}
            >
              <Grid container sx={{ padding: "24px"}}>
                {/* Editable Level Name */}
                <Grid item xs={12} md={12}>
                    <Typography sx={{mt:2, mb: 2}} variant="h6">Level {level.order}</Typography>

                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Level Name"
                    variant="outlined"
                    fullWidth
                    value={level.name}
                    onChange={(e) => handleLevelChange(levelIndex, 'name', e.target.value)}
                  />
                </Grid>

                <Grid container>
                  {activeLevelId === level._id && (
                    <NewQuestionForm
                      courseId={courseId}
                      unitId={unitId}
                      levelId={level._id}
                      levelOrder={level.order}
                      nextOrder={visibleQuestionCount + 1}
                      onCancel={handleCancelNewQuestion}
                      onSuccess={handleQuestionFormSuccess}
                    />
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2, placeSelf: 'center' }}
                    onClick={() => setActiveLevelId(level._id)}
                  >
                    Add New Question
                  </Button>

                  <Grid item xs={12} md={12}>
                    <Divider
                      sx={{
                        borderBottomWidth: 3, // Set the thickness
                        borderColor: "primary.main",
                        mt: 4,
                        mb: 4, // Set the color to blue
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Questions */}
                {orderedQuestions.map((question) => {
                  const isQuestionArchived = Boolean(question.isQuestionArchived);
                  if (!shouldDisplayQuestion(questionFilter, isQuestionArchived)) {
                    return null;
                  }
                  const questionIndex = level.questions.findIndex((item) => item._id === question._id);
                  if (questionIndex === -1) {
                    return null;
                  }
                  const isSelected = selectedQuestionsForLevel.includes(question._id);
                  const checkboxLabel = isQuestionArchived ? 'Select to restore question' : 'Select to archive question';
                  const questionOrderLabel = Number.isFinite(question.order) ? question.order : '-';

                  return (
                  <Grid key={question._id} sx={{ width:"100%" }}>
                    <br />
                    <Grid container>
                      <Grid>
                        <Typography sx={{ mt: 2, mb: 2 }} variant="h6">
                          Question: {questionOrderLabel}
                          {isQuestionArchived && (
                            <Typography
                              component="span"
                              variant="subtitle2"
                              color="error.main"
                              sx={{ ml: 1 }}
                            >
                              Archived
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                      <Grid sx={{ marginLeft: 'auto', marginTop: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              disabled={archivingLevelId === level._id || restoringLevelId === level._id}
                              onChange={(event) =>
                                toggleQuestionSelection(level._id, question._id, event.target.checked)
                              }
                            />
                          }
                          label={checkboxLabel}
                        />
                      </Grid>
                    </Grid>
                    <br />
                    <Grid container spacing={2} key={question._id}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Question Type</InputLabel>
                          <Select
                            value={question.questionType}
                            label="Question Type"
                            onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'questionType', e.target.value)}
                          >
                            {questionTypes.map((type) => (
                              <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Question"
                          variant="outlined"
                          fullWidth
                          value={question.question}
                          onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'question', e.target.value)}
                        />
                      </Grid>

                      {
                        ['ACIQ', 'CTA', 'CT', "ACIQM"].includes(question.questionType) &&
                        <div className='audio-container'>
                          <AudioPlayerComponent audioUrl={question.url} />
                          <form style={{ marginLeft: "20px" }} onSubmit={(e) => replaceAudio(e, question, 'url')}>
                            <label>Replace Audio {" "}</label>
                            <input type="file" accept=".mp3" onChange={(e) => handleFileSelectOfReplaceAudio(e)} ref={fileInputRef}  />
                            <button type="submit" style={{ marginLeft: "10px" }}>
                              Upload
                            </button>
                          </form>
                        </div> 
                      }

                      {/* {console.log("question options ----", question.Answer)} */}

                      {/* Render Options in Table Form */}
                    {!['ACMC', 'UVI', 'CT', 'LAR'].includes(question.questionType) && <Grid item xs={12}>
                        <Table sx={{ background: 'primary.lighterShade'}}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Option</TableCell>
                              <TableCell>Is Correct Answer</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                          {Array.isArray(question.options) && question.options.map((option, optionIndex) => (
                              <TableRow key={option._id}>
                                <TableCell>
                                  <TextField
                                    value={option}
                                    fullWidth
                                    onChange={(e) => handleOptionChange(levelIndex, questionIndex, optionIndex, e.target.value)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={isOptionCorrect(option, question.Answer, question.questionType)}
                                    onChange={(e) => handleAnswerChange(levelIndex, questionIndex, question.questionType, option)}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Grid>}


                      {
                        question && ['ACMC'].includes(question.questionType) ? <Grid item>
                              <Table sx={{ background: 'primary.lighterShade'}}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Option</TableCell>
                              <TableCell>Audio Clip</TableCell>
                              <TableCell>Is Correct Answer</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            { Object.entries(question.options||{}).map(([optionName, audioUrl], optionIndex)  => (
                              <TableRow key={optionName}>
                                <TableCell>
                                  {optionName}
                                </TableCell>
                                <TableCell>
                                  <div className='audio-container'>
                                    <AudioPlayerComponent audioUrl={audioUrl} />
                                    <form style={{ marginLeft: "20px" }} onSubmit={(e) => replaceAudio(e, question, `options.${optionName}`)}>
                                      <label>Replace Audio {" "}</label>
                                      <input type="file" accept=".mp3" onChange={(e) => handleFileSelectOfReplaceAudio(e)} ref={fileInputRef}  />
                                      <button type="submit" style={{ marginLeft: "10px" }}>
                                        Upload
                                      </button>
                                    </form>
                                  </div> 
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={ question.Answer === optionName }
                                    onChange={(e) => { handleAnswerChange(levelIndex, questionIndex, question.questionType, optionName) }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </Grid> : null
                      }
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Note Brief"
                          variant="outlined"
                          fullWidth
                          value={question.noteBrief}
                          onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'noteBrief', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Note Full"
                          variant="outlined"
                          fullWidth
                          value={question.noteFull}
                          onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'noteFull', e.target.value)}
                        />
                      </Grid>


                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Hint Type</InputLabel>
                          <Select
                            value={question.hintType}
                            label="Hint Type"
                            onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'hintType', e.target.value)}
                          >
                            {hintTypes.map((type) => (
                              <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>


                      {(question.questionType === "UVI" || question.questionType === "LAR" )&& 
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Phrase"
                          variant="outlined"
                          fullWidth
                          value={question.phrase}
                          onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'phrase', e.target.value)}
                        />
                      </Grid>
                      }


                    {question.hintType === "text" && <Grid item xs={12} md={8}>
                        <TextField
                          label="Hint"
                          variant="outlined"
                          fullWidth
                          value={question.hint}
                          onChange={(e) => handleQuestionChange(levelIndex, questionIndex, 'hint', e.target.value)}
                        />
                      </Grid>}

                      {question.hintType === "audio" ? <div>
                        <Grid item xs={12} md={8}>
                          <InputLabel sx={{marginLeft: 2, marginTop: 1}}>Hint Audio</InputLabel>
                        </Grid>
                        <div className='audio-container'>
                          <AudioPlayerComponent audioUrl={question.hintUrl} />
                          <form style={{ marginLeft: "20px" }} onSubmit={(e) => replaceAudio(e, question, `hintUrl`)}>
                            <label>Replace Audio {" "}</label>
                            <input type="file" accept=".mp3" onChange={(e) => handleFileSelectOfReplaceAudio(e)} ref={fileInputRef}  />
                            <button type="submit" style={{ marginLeft: "10px" }}>
                              Upload
                            </button>
                          </form>
                        </div> 
                      </div> : ""}


                      <Grid item xs={12} md={12}>
                        <Divider 
                          sx={{
                            borderBottomWidth: 3,  // Set the thickness
                            borderColor: 'primary.main',
                            mt: 4,
                            mb: 4   // Set the color to blue
                          }}
                        />                  
                      </Grid>
                    </Grid>
                    
                    <br />
                  </Grid>
                );
                })}
              </Grid>

              {/* Save Button for Each Level */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleUpdateLevel(level._id, levels[levelIndex], levelIndex)}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : `Save Level ${level.order}`}
                </Button>
                <ConfirmationModal
                  buttonLabel="Archive Selected"
                  loadingLabel="Archiving..."
                  confirmLabel="Confirm"
                  cancelLabel="Cancel"
                  title="Confirm Archive"
                  levelOrder={level.order}
                  selectedQuestions={selectedActiveQuestions}
                  disabled={!hasActiveSelections || archivingLevelId === level._id}
                  loading={archivingLevelId === level._id}
                  onConfirm={() => handleArchiveSelectedQuestions(level._id)}
                />
                <ConfirmationModal
                  buttonLabel="Restore Selected"
                  loadingLabel="Restoring..."
                  confirmLabel="Restore"
                  cancelLabel="Cancel"
                  title="Restore Questions"
                  description={`Level ${level.order} archived questions will be restored and appended to the end of the list.`}
                  levelOrder={level.order}
                  selectedQuestions={selectedArchivedQuestions}
                  disabled={!hasArchivedSelections || restoringLevelId === level._id}
                  loading={restoringLevelId === level._id}
                  onConfirm={() => handleRestoreSelectedQuestions(level._id)}
                />
              </Box>
            </Paper>
          );
          })}
        </Paper>

        <Snackbar
          open={open}
          autoHideDuration={4000} // Toast will auto close after 4 seconds
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Position of the toast
        >
          <Alert onClose={handleClose} severity={severity}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default UnitEditPage;
